
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { safeArrayCast } from "@/lib/universal-type-cast";

// Query key factory for fixtures
export const fixturesQueryKey = (leagueId?: string, season?: string) => 
  ['fixtures', leagueId || 'all', season || 'all-seasons'];

export const useFixturesQuery = (leagueId?: string, season?: string) => {
  const queryKey = fixturesQueryKey(leagueId, season);
  
  // @ts-ignore - Suppressing deep type instantiation error
  return useQuery({
    queryKey,
    queryFn: async () => {
      console.log(`Fetching fixtures for league: ${leagueId || 'all'}, season: ${season || 'all seasons'}`);
      
      try {
        // Build base query
        let query = supabase
          .from('team_matches')
          .select(`
            *,
            home_team:home_team_id(id, name, logo, captain_id),
            away_team:away_team_id(id, name, logo, captain_id)
          `);
          
        // Add filters conditionally
        if (leagueId) {
          query = query.eq('league_id', leagueId);
        }
          
        if (season) {
          console.log(`Filtering by season: ${season}`);
          // @ts-ignore - Type instantiation too deep
          query = query.eq('season', season);
        }

      // Execute the query
      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fixtures:', error);
        throw error;
      }

      console.log(`Fetched ${data?.length || 0} fixtures${leagueId ? ` for league ${leagueId}` : ''}${season ? ` and season ${season}` : ''}`);
      return safeArrayCast(data);
      } catch (error) {
        console.error('Error in fixtures query:', error);
        toast.error('Failed to load fixtures', { id: 'fixtures-error' });
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes stale time - increased from 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection time - increased from 15 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: 'always',
    retry: (failureCount, error) => {
      // Only retry network errors, not server errors
      if (error instanceof Error && error.message.includes('network')) {
        return failureCount < 3;
      }
      return false;
    }
  });
};

// Helper function to prefetch fixtures
export const prefetchFixtures = async (queryClient, leagueId?: string, season?: string) => {
  const key = fixturesQueryKey(leagueId, season);
  if (!queryClient.getQueryData(key)) {
    await queryClient.prefetchQuery({
      queryKey: key,
      queryFn: async () => {
        let query = supabase
          .from('team_matches')
          .select(`
            *,
            home_team:home_team_id(id, name, logo, captain_id),
            away_team:away_team_id(id, name, logo, captain_id)
          `);
          
        if (leagueId) query = query.eq('league_id', leagueId);
        if (season) query = query.eq('season', season);
          
        const { data, error } = await query;
        if (error) throw error;
        return data;
      },
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    });
  }
};
